import React from 'react';
import { useWizardKeyboard, KeyboardShortcutsHint } from '../../hooks/useWizardKeyboard';

/**
 * WizardButton - Standardized button component for wizard navigation
 * 
 * Variants:
 * - primary: Blue button for main actions (Continue, Submit)
 * - secondary: Outlined button for secondary actions (Previous, Cancel)
 * - success: Green button for final submission
 * - danger: Red button for destructive actions
 * 
 * @example
 * <WizardButton variant="primary" onClick={handleNext}>Continue</WizardButton>
 * <WizardButton variant="secondary" onClick={handlePrevious}>Previous</WizardButton>
 */
const WizardButton = ({
    children,
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    className = '',
    onClick,
    type = 'button',
    ...props
}) => {
    const baseClasses = `
    inline-flex items-center justify-center gap-2
    font-medium rounded-lg
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2
    disabled:opacity-50 disabled:cursor-not-allowed
  `;

    const sizeClasses = {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg'
    };

    const variantClasses = {
        primary: `
      bg-blue-600 text-white
      hover:bg-blue-700
      focus:ring-blue-500
    `,
        secondary: `
      bg-white text-gray-700
      border border-gray-300
      hover:bg-gray-50
      focus:ring-blue-500
    `,
        success: `
      bg-green-600 text-white
      hover:bg-green-700
      focus:ring-green-500
    `,
        danger: `
      bg-red-600 text-white
      hover:bg-red-700
      focus:ring-red-500
    `,
        ghost: `
      bg-transparent text-gray-600
      hover:bg-gray-100
      focus:ring-gray-500
    `
    };

    const classes = `
    ${baseClasses}
    ${sizeClasses[size] || sizeClasses.md}
    ${variantClasses[variant] || variantClasses.primary}
    ${className}
  `.replace(/\s+/g, ' ').trim();

    return (
        <button
            type={type}
            className={classes}
            disabled={disabled || loading}
            onClick={onClick}
            {...props}
        >
            {loading ? (
                <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Loading...</span>
                </>
            ) : (
                children
            )}
        </button>
    );
};

/**
 * WizardNavigation - Container for Previous/Next buttons
 * Handles responsive layout and keyboard navigation automatically
 */
export const WizardNavigation = ({
    onPrevious,
    onNext,
    previousLabel = 'Previous',
    nextLabel = 'Continue',
    canGoPrevious = true,
    canGoNext = true,
    loading = false,
    nextVariant = 'primary',
    showKeyboardHints = true,
    enableKeyboard = true,
    className = ''
}) => {
    // Enable keyboard navigation
    useWizardKeyboard({
        onNext: canGoNext && !loading ? onNext : undefined,
        onPrevious: canGoPrevious && !loading ? onPrevious : undefined,
        canGoNext: canGoNext && !loading,
        canGoPrevious: canGoPrevious && !loading,
        enabled: enableKeyboard
    });

    return (
        <div className={className}>
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 justify-center">
                {canGoPrevious && (
                    <WizardButton
                        variant="secondary"
                        onClick={onPrevious}
                        disabled={loading}
                    >
                        {previousLabel}
                    </WizardButton>
                )}
                <WizardButton
                    variant={nextVariant}
                    onClick={onNext}
                    disabled={!canGoNext}
                    loading={loading}
                >
                    {nextLabel}
                </WizardButton>
            </div>
            {showKeyboardHints && enableKeyboard && (
                <KeyboardShortcutsHint show={canGoNext || canGoPrevious} />
            )}
        </div>
    );
};

/**
 * StepIndicator - Consistent step badge styling
 */
export const StepIndicator = ({ current, total }) => (
    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
        Step {current} of {total}
    </span>
);

export default WizardButton;
